/* eslint-disable camelcase */
import { getConfig } from '../config';
import type { SubscriptionPaymentCycle } from '../entity/api/organization-billing';
import { getCookie } from '../shared/cookies';

const config = getConfig();

// Define dataLayer and the gtag function.
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    google_tag_manager?: any;
  }
}

export function setupGtagProxy() {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.dataLayer == null) {
    window.dataLayer = [];
  }

  window.gtag = function (...args: any[]) {
    window.dataLayer.push(args);
  };

  window.gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
}

const SST_BASE = 'https://sst.blym.co';
const SST_LOADER_BASE = 'https://load.sst.blym.co';
const CONTAINER_ID = 'desyivmsdwuzt';
const CONTAINER_QS = '9px=HR5IMDA%2BQCInTC9CJjghUAJHR1lVVQkYSRgZDgldDhk%3D';
const VISITOR_COOKIE_NAME = '_blym_uid';

function isSafariWithITP(): boolean {
  const match = /Version\/([0-9._]+)(.*Mobile)?.*Safari.*/.exec(navigator.userAgent);
  return match != null && Number.parseFloat(match[1]) >= 16.4;
}

function getVisitorId(): string | undefined {
  const uid = getCookie(VISITOR_COOKIE_NAME);
  if (uid != null && uid !== '') {
    return uid;
  }
  return undefined;
}

function buildGtmScriptUrl(): string {
  let containerId = CONTAINER_ID;
  let base = SST_LOADER_BASE;
  let biParam = '';

  try {
    if (isSafariWithITP()) {
      const uid = getVisitorId();
      containerId = containerId.replace(/([a-z]{8})$/, 'kp$1');
      base = SST_BASE;
      if (uid != null && uid !== '') {
        biParam = '&bi=' + encodeURIComponent(uid);
      }
    }
  } catch {
    // Fall through to default URL if detection fails
  }

  return `${base}/${containerId}.js?${CONTAINER_QS}${biParam}`;
}

export function initGTM() {
  if (typeof window === 'undefined' || config.env !== 'production') {
    return;
  }

  if (window.dataLayer == null) {
    window.dataLayer = [];
  }

  window.dataLayer.push({ event: 'gtm.js', 'gtm.start': Date.now() });

  if (document.querySelector('#gtm-script') != null) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  script.src = buildGtmScriptUrl();
  document.head.appendChild(script);
}

export function initGoogleTagManager() {
  // Initialize GTM
  initGTM();
  setupGtagProxy();
}

// Check if GTM is loaded
export const isGTMLoaded = function () {
  return (
    typeof window !== 'undefined' &&
    Array.isArray(window.dataLayer) &&
    typeof (window as Window).google_tag_manager !== 'undefined'
  );
};

export function buildUserData(user: {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}) {
  return {
    email_address: user.email ?? '',
    address: {
      first_name: user.firstName ?? '',
      last_name: user.lastName ?? '',
    },
  };
}

export function toRenewalType(paymentCycle: string): 'monthly' | 'annual' {
  return paymentCycle === 'year' ? 'annual' : 'monthly';
}

export function toPrice(value: number): number {
  return Number(value.toFixed(2));
}

export interface CheckoutPlanData {
  plan: string;
  pricePerMonth: number;
  priceDiscount: number;
  paymentCycle: SubscriptionPaymentCycle;
  currency: string;
}

const CHECKOUT_PLAN_KEY = 'blym_checkout_plan';

export function storeCheckoutPlan(data: CheckoutPlanData): void {
  try {
    localStorage.setItem(CHECKOUT_PLAN_KEY, JSON.stringify(data));
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded)
  }
}

export function peekCheckoutPlan(): CheckoutPlanData | null {
  try {
    const raw = localStorage.getItem(CHECKOUT_PLAN_KEY);
    if (raw == null) {
      return null;
    }
    return JSON.parse(raw) as CheckoutPlanData;
  } catch {
    return null;
  }
}

export function consumeCheckoutPlan(): void {
  try {
    localStorage.removeItem(CHECKOUT_PLAN_KEY);
  } catch {
    // localStorage may be unavailable
  }
}
